import {
  CommitteeConfigurationForm,
  CommitteeConfigurationFormValue,
} from 'src/screens/administration/configuration-management/committees-configuration/edit/CommitteeConfigurationForm'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { useParams } from 'react-router'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Committee_Config_Insert_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { CRITERIA_CONFIG_PROCESSES } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ROUTES } from 'src/routing/routes'
import { Utils } from 'src/shared/utils/Utils'
import {
  queryCommitteeConfigById,
  updateCommitteeConfigById,
} from 'src/screens/administration/configuration-management/committees-configuration/committeeConfigurationQueries'

import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

export const CommitteeConfigurationEditPage = () => {
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()

  const { processType, committeeId } = useParams()
  const process = Utils.getEnumFromString(processType, PROCESS)

  const [initialValues, setInitialValues] = useState<CommitteeConfigurationFormValue | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const fetchCriteriaGroupToEdit = useCallback(async () => {
    if (!committeeId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ committee_config: Query_Root['committee_config'] }>(queryCommitteeConfigById, { committeeId })
      .toPromise()

    if (error || !data?.committee_config?.[0]) {
      throw new Error('Resource not found')
    }

    const committeeConfig = data.committee_config[0]
    const committeeProcess = committeeConfig.processes[0]
    if (!committeeConfig || !CRITERIA_CONFIG_PROCESSES.includes(committeeProcess)) {
      throw new Error('Invalid state of fetched criteria config processes')
    }

    const values: CommitteeConfigurationFormValue = {
      deDesc: committeeConfig.descriptions['de'],
      enDesc: committeeConfig.descriptions['en'],
      frDesc: committeeConfig.descriptions['fr'],
      itDesc: committeeConfig.descriptions['it'],
      status: committeeConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      process: committeeProcess,
    }

    return { values }
  }, [committeeId, urqlClient])

  useEffect(() => {
    fetchCriteriaGroupToEdit()
      .then((data) => {
        setInitialValues(data.values)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchCriteriaGroupToEdit, notificationService])

  const onBackHandler = useCallback(() => {
    switch (process) {
      case 'PF_PGV':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationPfPgv.params({}))
      case 'PF_KAP':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationPfKap.params({}))
      case 'FACTSHEET':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationFactsheet.params({}))
      default:
        return ''
    }
  }, [navigate])

  const submitForm = () => {
    document.getElementById('committee-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  const onSave = async () => {
    originRef.current = 'SAVE'
    submitForm()
  }

  const onSaveAndBack = async () => {
    originRef.current = 'SAVE_AND_BACK'
    submitForm()
  }

  const handleSubmitLocal = useCallback(
    async (values: CommitteeConfigurationFormValue) => {
      const IdParsed = parseInt(committeeId as string)

      const insertInput: Committee_Config_Insert_Input = {
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        descriptions: {
          de: values.deDesc ? HtmlSanitizer.sanitize(values.deDesc) : '',
          en: values.enDesc ? HtmlSanitizer.sanitize(values.enDesc) : '',
          fr: values.frDesc ? HtmlSanitizer.sanitize(values.frDesc) : '',
          it: values.itDesc ? HtmlSanitizer.sanitize(values.itDesc) : '',
        },
      }

      const { data, error } = await urqlClient
        .mutation<{
          update_committee_config: Mutation_Root['update_committee_config']
        }>(updateCommitteeConfigById, {
          id: IdParsed,
          input: insertInput,
        })
        .toPromise()
      if (!error && data?.update_committee_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, urqlClient, onBackHandler, getMessage],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.committee.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <CommitteeConfigurationForm mode="EDIT" onSubmit={handleSubmitLocal} initialValues={initialValues} />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
